import { matchesOpenedProjectIdentity } from './projectSnapshot/OpenedProjectIdentity';
import { inspectStoredProjectIdentity } from './projectSnapshot/ProjectIdentity';
import { ProjectOpenHistory } from './ProjectOpenHistory';
import type { ProjectLoadReport } from '@/utils/projectLoadProgress';
import { z } from 'zod';
import type { IStorageProvider } from '@/interfaces/storage';
import type { ProjectDto, ChipExportBundle } from '@/schemas/chip';
import { ChipIdSchema } from '@/schemas/common';
import { ProjectSaveWorkerClient } from './ProjectSaveWorkerClient';
import { latestProjectBytes } from './automaticProjectMigration';
import {
  CapturedProjectSnapshot,
  snapshotFromDto,
  type ProjectSnapshotPacket,
} from './projectSnapshot/ProjectSnapshot';
import {
  decodeCurrentProjectDto,
  decodeProjectSnapshot,
  encodeCurrentProjectDto,
} from './projectSnapshot/CurrentProjectFormat';

const PROJECT_PREFIX = 'project:';
const LAST_PROJECT_KEY = 'lastProject';
interface WorkerEditingSession {
  kind: 'worker';
  id: string;
  client: ProjectSaveWorkerClient;
  ready: Promise<void>;
  current: true;
}
export interface LoadedCurrentProject {
  readonly project: ProjectDto;
  /** Starts from the immutable bytes read with this project, not later DTO mutations. */
  beginEditing(local: ProjectDto | CapturedProjectSnapshot): void;
}

export interface CurrentPersistenceOptions {
  createCurrentSaveWorker?: () => ProjectSaveWorkerClient;
}
const ChipExportBundleEnvelopeSchema = z.object({
  version: z.literal(1),
  exportedAt: z.string(),
  rootChipId: ChipIdSchema,
  chips: z.array(z.any()),
});

/** Writes the latest format; older supported sources migrate before editing. */
export class CurrentPersistenceService {
  readonly currentSnapshotsEnabled = true;
  private editSessions = new Map<string, WorkerEditingSession>();
  private saveWorker?: ProjectSaveWorkerClient;
  private workerSequence = 0;
  private saveQueue: Promise<void> = Promise.resolve();
  private readonly openHistory: ProjectOpenHistory;
  private readonly historyWrites = new Set<Promise<void>>();
  constructor(
    private readonly storage: IStorageProvider,
    private readonly options: CurrentPersistenceOptions = {},
  ) {
    this.openHistory = new ProjectOpenHistory(storage);
  }

  async readProjectLastOpened(
    names: readonly string[],
    signal?: AbortSignal,
  ): Promise<Record<string, number>> {
    await Promise.all([...this.historyWrites]);
    signal?.throwIfAborted();
    const values: [string, number][] = [];
    const records = await this.openHistory.readMany(names);
    for (const [name, record] of records) {
      signal?.throwIfAborted();
      try {
        // History supports the full valid manifest range without relaxing the
        // smaller privacy/attempt chooser hint or decoding runtime graphs.
        const stored = await this.storage.readUnmigrated?.(PROJECT_PREFIX + name);
        if (await matchesOpenedProjectIdentity(stored?.value, record.mainChipId, signal))
          values.push([name, record.openedAt]);
      } catch {
        signal?.throwIfAborted();
      }
    }
    signal?.throwIfAborted();
    return Object.fromEntries(values);
  }

  recordProjectOpened(name: string, mainChipId: string, openedAt: number) {
    const write = this.openHistory.record(name, mainChipId, openedAt);
    this.historyWrites.add(write);
    return write.finally(() => {
      this.historyWrites.delete(write);
    });
  }

  watchProjectOpenHistory(onChange: () => void) {
    return this.openHistory.watch(onChange);
  }

  forgetProjectOpened(name: string) {
    return this.openHistory.forget(name);
  }

  async readProjectIdentity(
    name: string,
    signal?: AbortSignal,
  ): Promise<string | undefined> {
    signal?.throwIfAborted();
    const stored = await this.storage.readUnmigrated?.(PROJECT_PREFIX + name);
    signal?.throwIfAborted();
    return inspectStoredProjectIdentity(stored?.value, signal);
  }

  beginEditingProject(
    local: ProjectDto | CapturedProjectSnapshot,
    stored: ProjectDto | null,
  ): void {
    this.beginCurrentSession(
      local instanceof CapturedProjectSnapshot ? local : snapshotFromDto(local),
      stored,
    );
  }

  *beginEditingProjectSteps(
    local: ProjectDto | CapturedProjectSnapshot,
    stored: ProjectDto | null,
  ): Generator<void> {
    this.beginEditingProject(local, stored);
  }

  saveProjectChanges(name: string, dto: ProjectDto): Promise<void> {
    return this.saveCurrentSnapshot(name, snapshotFromDto(dto));
  }

  saveCurrentSnapshot(name: string, capture: CapturedProjectSnapshot): Promise<void> {
    const session = this.editSessions.get(name);
    if (!session)
      return Promise.reject(new Error('Reload this project before saving it.'));
    const jobId = `job-${++this.workerSequence}`,
      snapshot = capture.consumeForWorker();
    const prepared = session.client.request(
      { kind: 'prepare-current', sessionId: session.id, jobId, snapshot },
      this.snapshotTransfers(snapshot),
    );
    void prepared.catch(() => {});
    const save = this.saveQueue.then(() =>
      this.commitWorkerChanges(name, session, jobId, prepared),
    );
    this.saveQueue = save.catch(() => {});
    return save;
  }

  private snapshotTransfers(packet: ProjectSnapshotPacket): Transferable[] {
    return packet.blocks.records.map((record) => record.buffer);
  }

  private beginCurrentSession(
    capture: CapturedProjectSnapshot,
    stored: ProjectDto | Blob | null,
  ): void {
    if (
      !this.storage.supportsBlobs ||
      !this.storage.updateAsync ||
      (!this.options.createCurrentSaveWorker && typeof Worker === 'undefined')
    )
      throw new Error('Current project saving requires browser storage and workers.');
    const local = capture.consume(),
      baseline =
        stored instanceof Blob
          ? stored
          : stored
            ? snapshotFromDto(stored).consume()
            : null;
    if (this.saveWorker?.isFailed) this.saveWorker = undefined;
    const client = (this.saveWorker ??=
      this.options.createCurrentSaveWorker?.() ??
      new ProjectSaveWorkerClient(
        () =>
          new Worker(new URL('./currentProjectSave.worker.ts', import.meta.url), {
            type: 'module',
          }),
      ));
    const id = `session-${++this.workerSequence}`;
    this.retireEditingSessions();
    const ready = client.request(
      { kind: 'begin-current', sessionId: id, local, stored: baseline },
      [
        ...this.snapshotTransfers(local),
        ...(baseline && !(baseline instanceof Blob)
          ? this.snapshotTransfers(baseline)
          : []),
      ],
    );
    void ready.catch(() => {});
    this.editSessions.set(capture.name, {
      kind: 'worker',
      id,
      client,
      ready,
      current: true,
    });
  }

  private retireEditingSessions(): void {
    for (const session of this.editSessions.values())
      void session.client
        .request({ kind: 'retire', sessionId: session.id })
        .catch(() => {});
    this.editSessions.clear();
  }

  private async commitWorkerChanges(
    name: string,
    session: WorkerEditingSession,
    jobId: string,
    prepared: Promise<void>,
  ): Promise<void> {
    try {
      await session.ready;
      await prepared;
      let committedAttempt = '';
      await this.storage.updateAsync!<unknown>(PROJECT_PREFIX + name, async (raw) => {
        const attemptId = `attempt-${++this.workerSequence}`;
        const blob = await session.client.request<Blob>({
          kind: 'merge',
          jobId,
          attemptId,
          raw,
        });
        committedAttempt = attemptId;
        return blob;
      });
      await session.client.request({ kind: 'ack', jobId, attemptId: committedAttempt });
      await this.storage.save(LAST_PROJECT_KEY, name);
    } finally {
      // Failed writes never ACK; discard also releases old-session jobs after switches.
      await session.client.request({ kind: 'discard', jobId }).catch(() => {});
    }
  }

  watchProject(name: string, onChange: () => void): () => void {
    return this.storage.subscribe?.(PROJECT_PREFIX + name, onChange) ?? (() => {});
  }

  async saveProject(name: string, projectDto: ProjectDto): Promise<void> {
    await this.storage.save(
      PROJECT_PREFIX + name,
      await encodeCurrentProjectDto(projectDto),
    );
    await this.storage.save(LAST_PROJECT_KEY, name);
  }

  /** Compare-and-insert: concurrent tabs never overwrite an existing local copy. */
  async importSharedCopy(project: ProjectDto, signal?: AbortSignal): Promise<string> {
    const collision = new Error('Shared copy name already exists');
    const base = `${project.name.slice(0, 120)} (shared copy)`;
    for (let suffix = 1; suffix <= 1000; suffix++) {
      const name = suffix === 1 ? base : `${base} ${suffix}`;
      signal?.throwIfAborted();
      const encoded = await encodeCurrentProjectDto({
        ...project,
        name,
        startPaused: true,
      });
      try {
        await this.storage.update<unknown>(PROJECT_PREFIX + name, (current) => {
          signal?.throwIfAborted();
          if (current !== null) throw collision;
          return encoded;
        });
        return name;
      } catch (error) {
        if (error !== collision) throw error;
      }
    }
    throw new Error('Too many shared copies with this name. Rename a local copy first.');
  }

  async getLastProjectName(): Promise<string | null> {
    return this.storage.load<string>(LAST_PROJECT_KEY);
  }

  async loadProject(name: string): Promise<ProjectDto | null> {
    return (await this.loadProjectForEditing(name))?.project ?? null;
  }

  /** Pin read-time bytes explicitly; the ordinary DTO API keeps call-time capture semantics. */
  async loadProjectForEditing(
    name: string,
    signal?: AbortSignal,
    report?: ProjectLoadReport,
  ): Promise<LoadedCurrentProject | null> {
    const raw = await this.readProjectSource(name, signal, report);
    if (raw === null) return null;
    const baseline = raw;
    const project = await decodeCurrentProjectDto(baseline, report, signal);
    return {
      project,
      beginEditing: (local) =>
        this.beginCurrentSession(
          local instanceof CapturedProjectSnapshot ? local : snapshotFromDto(local),
          baseline,
        ),
    };
  }

  private async readProjectSource(
    name: string,
    signal?: AbortSignal,
    report?: ProjectLoadReport,
  ): Promise<Blob | null> {
    const key = PROJECT_PREFIX + name;
    const snapshot = await this.storage.readUnmigrated?.(key);
    const raw = snapshot ? snapshot.value : await this.storage.load<unknown>(key);
    if (snapshot ? snapshot.source === 'missing' : raw === null) return null;
    signal?.throwIfAborted();
    const latest = await latestProjectBytes(raw, signal, report);
    if (latest !== raw) {
      if (!snapshot || !this.storage.migrateProject)
        throw new Error(
          'This storage provider cannot safely upgrade this project. Its original is preserved.',
        );
      signal?.throwIfAborted();
      await this.storage.migrateProject(snapshot, latest, signal);
    }
    return latest;
  }

  /** Validate the stored container without expanding runtime node DTO arrays. */
  async exportProjectFile(name: string): Promise<Blob | null> {
    const raw = await this.readProjectSource(name);
    if (raw === null) return null;
    await decodeProjectSnapshot(raw);
    return raw;
  }

  async deleteProject(
    name: string,
    options?: { preserveLegacy?: boolean },
  ): Promise<void> {
    await this.storage.delete(PROJECT_PREFIX + name, options);
    await this.openHistory.forget(name);
    const last = await this.getLastProjectName();
    if (last === name) {
      await this.storage.delete(LAST_PROJECT_KEY);
    }
  }

  async renameProject(oldName: string, newName: string): Promise<void> {
    // Resolve migration first, then decode the exact revision used for the move.
    await this.readProjectSource(oldName);
    const source =
      this.storage.moveProject && this.storage.readUnmigrated
        ? await this.storage.readUnmigrated(PROJECT_PREFIX + oldName)
        : undefined;
    const dto = source
      ? await decodeCurrentProjectDto(source.value as Blob)
      : await this.loadProject(oldName);
    if (!dto) throw new Error(`Project not found: ${oldName}`);
    const opened = await this.openHistory.read(oldName);
    const sessionName = this.editSessions.has(oldName) ? oldName : dto.name;
    dto.name = newName;
    const encoded = await encodeCurrentProjectDto(dto);
    // The provider's compare-and-swap update rechecks collisions at commit time.
    if (source)
      await this.storage.moveProject!(source, PROJECT_PREFIX + newName, encoded);
    else
      await this.storage.update(PROJECT_PREFIX + newName, (existing) => {
        if (oldName !== newName && existing !== null)
          throw Error('A project with this name already exists.');
        return encoded;
      });
    // Renaming current bytes is not an explicit deletion of a retained legacy
    // original. It remains listed under its old name for recovery/conversion.
    if (!source && oldName !== newName)
      await this.deleteProject(oldName, { preserveLegacy: true });
    const session = this.editSessions.get(sessionName);
    if (session) {
      this.editSessions.delete(sessionName);
      this.editSessions.set(newName, session);
    }
    // Ancillary metadata failure must not undo a completed storage move in memory.
    try {
      await this.storage.save(LAST_PROJECT_KEY, newName);
      if (source && oldName !== newName) await this.openHistory.forget(oldName);
      await this.openHistory.forget(newName);
      if (opened?.mainChipId === dto.mainChipId)
        await this.openHistory.record(newName, dto.mainChipId, opened.openedAt);
    } catch {
      /* The renamed project remains available by its new name. */
    }
  }

  async copyProject(sourceName: string, newName: string): Promise<void> {
    const dto = await this.loadProject(sourceName);
    if (!dto) throw new Error(`Project not found: ${sourceName}`);
    dto.name = newName;
    await this.saveProject(newName, dto);
    await this.openHistory.forget(newName);
  }

  async listProjects(): Promise<string[]> {
    const keys = await this.storage.listKeys(PROJECT_PREFIX);
    return keys.map((k) => k.slice(PROJECT_PREFIX.length));
  }

  validateChipExportBundleEnvelope(data: unknown): ChipExportBundle {
    return ChipExportBundleEnvelopeSchema.parse(data) as ChipExportBundle;
  }
}
